import React, { useEffect, useRef, useState } from "react";
import { createChart } from "lightweight-charts";

const FIVE_MINUTES = 5 * 60;

const Index5CoinChart = ({ chartPrice }) => {
  const wrapperRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const containerRef = useRef(null);
  const candleMapRef = useRef({});
  const [isFullscreen, setIsFullscreen] = useState(false);

  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isMobile = /Mobi|Android/i.test(navigator.userAgent);

  const getCandleStartTime = (timestamp) =>
    Math.floor(timestamp / FIVE_MINUTES) * FIVE_MINUTES;

  const getThemeOptions = (theme) => {
    const isDark = theme === "dark";
    return {
      layout: {
        background: {
          type: "solid",
          color: isDark ? "#1e1e1e" : "#ffffff",
        },
        textColor: isDark ? "#ffffff" : "#000000",
      },
      grid: {
        vertLines: {
          color: isDark ? "#2f2f2f" : "#e1e1e1",
        },
        horzLines: {
          color: isDark ? "#2f2f2f" : "#e1e1e1",
        },
      },
    };
  };

  const resizeChart = () => {
    if (chartRef.current && containerRef.current) {
      chartRef.current.applyOptions({
        width: containerRef.current.clientWidth,
        height: containerRef.current.clientHeight,
      });
    }
  };

  useEffect(() => {
    if (!containerRef.current) return;

    const theme = document.body.getAttribute("data-theme-version") || "light";

    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height: containerRef.current.clientHeight,
      ...getThemeOptions(theme),
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
      },
    });

    const series = chart.addCandlestickSeries({
      upColor: "#26a69a",
      downColor: "#ef5350",
      wickUpColor: "#26a69a",
      wickDownColor: "#ef5350",
      borderVisible: false,
    });

    chartRef.current = chart;
    seriesRef.current = series;

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "data-theme-version"
        ) {
          const newTheme =
            document.body.getAttribute("data-theme-version") || "light";
          chart.applyOptions(getThemeOptions(newTheme));
        }
      }
    });

    observer.observe(document.body, { attributes: true });

    const handleFullscreenChange = () => {
      const isInFullscreen = document.fullscreenElement === wrapperRef.current;
      setIsFullscreen(isInFullscreen);
      setTimeout(resizeChart, 300);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    window.addEventListener("resize", resizeChart);

    resizeChart();

    return () => {
      chart.remove();
      observer.disconnect();
      window.removeEventListener("resize", resizeChart);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    if (!Array.isArray(chartPrice) || chartPrice.length === 0) return;

    const updatedMap = { ...candleMapRef.current };

    chartPrice.forEach((point) => {
      const chartPoint = point.chartPoint || point;
      const bucketTime = getCandleStartTime(chartPoint.time);
      const existing = updatedMap[bucketTime];

      if (!existing) {
        updatedMap[bucketTime] = {
          time: bucketTime,
          open: chartPoint.open,
          high: chartPoint.high,
          low: chartPoint.low,
          close: chartPoint.close,
        };
      } else {
        existing.high = Math.max(existing.high, chartPoint.high);
        existing.low = Math.min(existing.low, chartPoint.low);
        existing.close = chartPoint.close;
      }
    });

    candleMapRef.current = updatedMap;
    const sorted = Object.values(updatedMap).sort((a, b) => a.time - b.time);
    if (seriesRef.current) {
      seriesRef.current.setData(sorted);
    }
  }, [chartPrice]);

  const toggleFullScreen = async () => {
    const el = wrapperRef.current;
    if (!el) return;

    try {
      if (document.fullscreenEnabled && !isIOS) {
        if (!document.fullscreenElement) {
          await el.requestFullscreen();
        } else {
          await document.exitFullscreen();
        }
        return;
      }

      // Simulated fullscreen fallback
      setIsFullscreen((prev) => {
        setTimeout(resizeChart, 300);
        return !prev;
      });
    } catch (error) {
      console.warn("Fullscreen error. Falling back:", error);
      setIsFullscreen((prev) => {
        setTimeout(resizeChart, 300);
        return !prev;
      });
    }
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && isFullscreen && !isMobile && !isIOS) {
        setIsFullscreen(false);
        setTimeout(resizeChart, 300);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isFullscreen, isMobile, isIOS]);

  return (
    <div
      ref={wrapperRef}
      style={{
        position: isFullscreen ? "fixed" : "relative",
        top: isFullscreen ? 0 : "auto",
        left: isFullscreen ? 0 : "auto",
        width: "100%",
        height: isFullscreen ? "100vh" : isMobile || isIOS ? "50vh" : "400px", // ✅ Changed to 50vh for mobile
        backgroundColor: "#ffffff",
        zIndex: isFullscreen ? 9999999 : "auto",
      }}
    >
      {/* ✅ Hide fullscreen button on mobile */}
      {!isMobile && !isIOS && (
        <button
          onClick={toggleFullScreen}
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            zIndex: 9999999,
            padding: "8px 12px",
            fontSize: "14px",
            borderRadius: "4px",
            backgroundColor: "#0d6efd",
            color: "#fff",
            border: "none",
            cursor: "pointer",
          }}
          title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
        >
          <i
            className={`bi ${
              isFullscreen ? "bi-fullscreen-exit" : "bi-arrows-fullscreen"
            }`}
          ></i>
        </button>
      )}

      {/* Chart container */}
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "100%",
        }}
      />
    </div>
  );
};

export default Index5CoinChart;
